import { type FormEvent, useState } from 'react'
import { useAuth } from '../../lib/auth-context.js'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../../lib/error-copy.js'
import { LOADING } from '../../lib/labels.js'
import { useToast } from '../../lib/toast.js'
import { SettingsSectionCard, SettingsUnavailableNotice } from './SettingsSectionCard.js'

export function SettingsEmailSection() {
  const { session, updateEmail } = useAuth()
  const toast = useToast()

  const [newEmail, setNewEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!session) {
    return (
      <SettingsSectionCard
        title="ログイン用メールアドレス"
        description="ログインに使うメールアドレスを変更します。"
      >
        <SettingsUnavailableNotice message="アカウント登録後に利用できます。" />
      </SettingsSectionCard>
    )
  }

  const currentEmail = session.user.email ?? ''

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmed = newEmail.trim()
    if (!trimmed) {
      setError('メールアドレスを入力してください')
      return
    }
    if (trimmed === currentEmail) {
      setError('現在と同じメールアドレスです')
      return
    }

    setSubmitting(true)
    try {
      await updateEmail(trimmed)
      setNewEmail('')
      toast.success('確認メールを送信しました。メール内のリンクから変更を完了してください')
    } catch (err) {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SettingsSectionCard
      title="ログイン用メールアドレス"
      description="ログインに使うメールアドレスを変更します。"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <p className="text-sm font-medium mb-1">現在のメールアドレス</p>
          <p className="text-sm text-muted-foreground">{currentEmail || '（未設定）'}</p>
        </div>
        <div>
          <label htmlFor="settings-new-email" className="block text-sm font-medium mb-1">
            新しいメールアドレス
          </label>
          <input
            id="settings-new-email"
            type="email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
          />
          <p className="text-xs text-muted-foreground mt-1">
            変更後、新しいメールアドレス宛に確認メールが届きます。リンクを開くと変更が完了します。
          </p>
        </div>
        {error && (
          <p className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2">
            {error}
          </p>
        )}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={submitting || !newEmail.trim()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {submitting ? LOADING.sending : 'メールアドレスを変更'}
          </button>
        </div>
      </form>
    </SettingsSectionCard>
  )
}
